import { useCallback, useEffect, useRef, useState } from 'react'
import type { DependencyList } from 'react'
import { imHintergrund } from './api'
import { t } from './sprache'

/**
 * **Ein gescheiterter Abruf ist nicht leer** — 7.2 Abschnitt 39, R1210–R1213.
 *
 * Der Befund vom 2026-10-06: Antwortete der Dienst einen Augenblick lang
 * nicht, stand im Zuordnungsfenster *„Keine Bewertungsebene führt einen
 * Positionsplan"* und im Belegordner *„In diesem Ordner liegt kein Beleg."* —
 * Aussagen über den Bestand, die niemand geprüft hatte. 67 Stellen auf 24
 * Seiten machten aus einem Fehler eine leere Liste.
 *
 * **Drei Zustände, nicht zwei** (R1212): Wer über diesen Baustein holt, kann
 * „ausgefallen" nicht mehr mit „da, aber leer" verwechseln, weil er für einen
 * Ausfall nie eine leere Liste bekommt.
 */
export type Abruf<T> = {
  /** Der Wert, sobald er da ist — `null`, solange er lädt, nichts zu holen ist oder er ausfiel. */
  wert: T | null
  laedt: boolean
  /** Der Abruf ist gescheitert; am Ort steht dann „Ließ sich nicht laden" (R1210). */
  ausgefallen: boolean
  /** Holt nur diesen Teil neu — der Knopf *Erneut versuchen* (R1210). */
  erneut: () => void
  /** Setzt den Wert von Hand, etwa nach einem Speichern, das die neue Fassung schon zurückgibt. */
  setze: (wert: T | null) => void
}

type Stand<T> = { wert: T | null; laedt: boolean; ausgefallen: boolean }

/**
 * **Ein Hauptabruf** (R1211): Was er holt, zeigt die Seite als Bestand. Fällt
 * er aus, meldet die Schnittstelle die Störung im Störungsfenster (R1005), und
 * der Baustein hält fest, dass er ausgefallen ist.
 *
 * @param abruf `null`, wenn gerade nichts zu holen ist — etwa ohne gewählten Ordner.
 * @param abhaengig Wann neu geholt wird, wie bei `useEffect`.
 * @param frisch Beim Neuholen den alten Wert sofort verwerfen, statt ihn bis
 *   zur Antwort stehen zu lassen — wo der alte Wert zu etwas anderem gehört
 *   (ein anderer Ordner, eine andere Periode).
 */
export function useAbruf<T>(
  abruf: (() => Promise<T>) | null,
  abhaengig: DependencyList,
  frisch = false,
): Abruf<T> {
  const [stand, setStand] = useState<Stand<T>>({ wert: null, laedt: abruf !== null, ausgefallen: false })
  const [versuch, setVersuch] = useState(0)

  // Der Abruf der letzten Darstellung — er schließt über die Werte, die
  // `abhaengig` nennt, und wird nur geholt, wenn sich einer davon ändert.
  const aktuell = useRef(abruf)
  aktuell.current = abruf

  useEffect(() => {
    const holen = aktuell.current
    if (holen === null) {
      setStand({ wert: null, laedt: false, ausgefallen: false })
      return
    }

    let gilt = true
    setStand((s) => ({ wert: frisch ? null : s.wert, laedt: true, ausgefallen: false }))
    holen()
      .then((wert) => gilt && setStand({ wert, laedt: false, ausgefallen: false }))
      .catch(() => gilt && setStand({ wert: null, laedt: false, ausgefallen: true }))

    return () => {
      gilt = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...abhaengig, versuch])

  const erneut = useCallback(() => setVersuch((v) => v + 1), [])
  const setze = useCallback((wert: T | null) => setStand({ wert, laedt: false, ausgefallen: false }), [])

  return { ...stand, erneut, setze }
}

/**
 * **Ein Abruf im Hintergrund** (R1007, geschärft durch R1211): Namen zu
 * Kennungen, Auswahllisten — was eine Seite bequemer macht, aber keine Aussage
 * über den Bestand trägt. Er meldet keine Störung, versucht es einmal erneut
 * und gibt bei einem Ausfall den **genannten Ersatz** zurück (R1212) — etwa
 * eine leere Auswahlliste. Bis zur Antwort gilt derselbe Ersatz.
 */
export function useHintergrundabruf<T>(
  abruf: (() => Promise<T>) | null,
  abhaengig: DependencyList,
  ersatz: T,
): T {
  const [wert, setWert] = useState<T>(ersatz)

  const aktuell = useRef(abruf)
  aktuell.current = abruf
  const ersatzwert = useRef(ersatz)
  ersatzwert.current = ersatz

  useEffect(() => {
    const holen = aktuell.current
    if (holen === null) {
      setWert(ersatzwert.current)
      return
    }

    let gilt = true
    imHintergrund(holen)
      .then((w) => gilt && setWert(w))
      .catch(() => gilt && setWert(ersatzwert.current))

    return () => {
      gilt = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, abhaengig)

  return wert
}

/**
 * **Was am Ort steht, wenn ein Abruf ausfiel** (R1210): *Ließ sich nicht
 * laden.* und der Knopf *Erneut versuchen* — statt des Satzes, der einen
 * leeren Bestand meldet. Das Störungsfenster sagt, was geschah; dieser Satz
 * bleibt stehen, wenn es weggeklickt ist.
 */
export function Abrufausfall({ abruf, satz }: { abruf: Pick<Abruf<unknown>, 'erneut'>; satz?: string }) {
  return (
    <p className="hinweis abrufausfall" role="status">
      {satz ?? t('abruf.ausgefallen')}{' '}
      <button type="button" className="schmal" onClick={abruf.erneut}>
        {t('abruf.erneut')}
      </button>
    </p>
  )
}
